import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { CompactMoneyAmount } from "@/shared/ui/CompactMoneyAmount";
import { BarChart } from "@/shared/ui/BarChart";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import type { ChartConfig } from "@/shared/ui/ChartContainer";
import { Button, Card, CardContent, CardHeader, CardTitle } from "@devhop/ui";
import { useSearch } from "@tanstack/react-router";
import { format, subDays } from "date-fns";
import { BanknoteIcon, DownloadIcon, GraduationCapIcon } from "lucide-react";
import { useCallback, useMemo } from "react";
import { useInstructorSessionByInstructorQuery } from "../api/queries";
import {
  InstructorSessionReportFilter,
  type InstructorSessionReportSearch,
} from "../ui/InstructorSessionReportFilter";
import { InstructorSessionReportTable } from "../ui/InstructorSessionReportTable";
import {
  ReportSummaryStatCard,
  ReportSummaryStatGrid,
  reportSummaryCount,
} from "../ui/ReportSummaryStatCard";

function useReportQuery(search: InstructorSessionReportSearch) {
  const defaultFrom = format(subDays(new Date(), 30), "yyyy-MM-dd");
  const defaultTo = format(new Date(), "yyyy-MM-dd");
  const dateFrom = search.dateFrom ?? defaultFrom;
  const dateTo = search.dateTo ?? defaultTo;

  const summaryQuery = useMemo(
    () => ({
      dateFrom,
      dateTo,
      instructorId: search.instructorId,
    }),
    [dateFrom, dateTo, search.instructorId],
  );

  return { dateFrom, dateTo, summaryQuery };
}

function escapeCsvCell(value: string | number): string {
  const s = String(value);
  if (
    s.includes(",") ||
    s.includes('"') ||
    s.includes("\n") ||
    s.includes("\r")
  ) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

export function InstructorSessionReportPage() {
  const search = useSearch({
    from: "/app/reports/instructor-session",
  }) as InstructorSessionReportSearch;

  const { dateFrom, dateTo, summaryQuery } = useReportQuery(search);

  const { data: byInstructor = [], isLoading: byInstructorLoading } =
    useInstructorSessionByInstructorQuery(summaryQuery);

  const summary = useMemo(
    () => ({
      sessionsTaught: byInstructor.reduce((a, r) => a + r.sessionsTaught, 0),
      estimatedPayout: byInstructor.reduce((a, r) => a + r.estimatedPayout, 0),
    }),
    [byInstructor],
  );

  const exportCsv = useCallback(() => {
    const header =
      "ຄູສອນ,ລວມຄັ້ງ,ຄລາດສ໌,ວິຊາສ່ວນຕົວ,ສະຕູດິໂອ,ນອກສະຖານທີ່,partner_class_only,ຊົ່ວໂມງລວມ,ຊົ່ວໂມງກຸ່ມ,ຊົ່ວໂມງສ່ວນຕົວ,ຊົ່ວໂມງ partner,ຈຳນວນກຸ່ມ_ກີບ,ຈຳນວນສ່ວນຕົວ_ກີບ,ຈຳນວນ partner_ກີບ,ຖານກຸ່ມ,ຖານສ່ວນຕົວ,ຖານ partner,ຄ່າຈ້າງກຸ່ມ_ກີບ,ຄ່າຈ້າງສ່ວນຕົວ_ກີບ,ຄ່າຈ້າງ partner_ກີບ,ປະເມີນຄ່າຈ້າງລວມ (ກີບ)";
    const rows = byInstructor.map(
      (r) =>
        `${escapeCsvCell(r.instructorName)},${escapeCsvCell(r.sessionsTaught)},${escapeCsvCell(r.generalClassSessions)},${escapeCsvCell(r.privateLessonSessions)},${escapeCsvCell(r.studioClassSessions)},${escapeCsvCell(r.offSiteClassSessions)},${escapeCsvCell(r.partnerInstructorOnlyClassSessions)},${escapeCsvCell(r.billableHoursTotal)},${escapeCsvCell(r.billableHoursGeneralClass)},${escapeCsvCell(r.billableHoursPrivateLesson)},${escapeCsvCell(r.billableHoursPartner)},${escapeCsvCell(r.generalClassRatePerHour)},${escapeCsvCell(r.privateLessonRatePerHour)},${escapeCsvCell(r.partnerRatePerHour)},${escapeCsvCell(r.generalClassPayoutBasis)},${escapeCsvCell(r.privateLessonPayoutBasis)},${escapeCsvCell(r.partnerPayoutBasis)},${escapeCsvCell(r.estimatedPayoutGeneralClass)},${escapeCsvCell(r.estimatedPayoutPrivateLesson)},${escapeCsvCell(r.estimatedPayoutPartner)},${escapeCsvCell(r.estimatedPayout)}`,
    );
    const csv = [header, ...rows].join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `instructor-session-report-${dateFrom}-${dateTo}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [byInstructor, dateFrom, dateTo]);

  const barChartData = useMemo(
    () =>
      byInstructor.map((r) => ({
        instructor: r.instructorName,
        sessionsTaught: r.sessionsTaught,
      })),
    [byInstructor],
  );

  const barChartConfig: ChartConfig = {
    sessionsTaught: {
      label: "ຈຳນວນຄັ້ງ",
      color: "var(--chart-1)",
    },
  };

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
            { label: "ລາຍງານ ຊົ່ວໂມງຮຽນ ຄູສອນ" },
          ]}
        />
      </Header>

      <Main>
        <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="font-bold text-2xl">ລາຍງານ ຊົ່ວໂມງຮຽນ ຄູສອນ</h1>
            <p className="mt-1 text-muted-foreground text-sm">
              ຈຳນວນຄັ້ງ ລວມຄລາດສ໌ + ວິຊາສ່ວນຕົວ — ປະເມີນຄ່າຈ້າງຄິດຈາກຊົ່ວໂມງເທື່ອງຈິງ ×
              ອັດຕາຕໍ່ຊົ່ວໂມງ (ກຸ່ມ / ສ່ວນຕົວ / Partner) ຕາມການຕັ້ງຄ່າຄູ; ຖັນການນັບ Partner
              ສະເພາະຄລາດສ໌ partner_instructor_only
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={exportCsv}
            disabled={byInstructor.length === 0}
          >
            <DownloadIcon className="mr-2 size-4" />
            ດາວໂຫລດ CSV
          </Button>
        </div>

        <div className="mb-6 flex flex-col rounded-xl border bg-card pt-2">
          <InstructorSessionReportFilter />
        </div>

        <ReportSummaryStatGrid columns={2}>
          <ReportSummaryStatCard
            title="ຈຳນວນຄັ້ງສອນລວມ"
            icon={GraduationCapIcon}
            tone="students"
            isLoading={byInstructorLoading}
            value={reportSummaryCount(summary.sessionsTaught)}
          />
          <ReportSummaryStatCard
            title="ປະເມີນຄ່າຈ້າງ"
            icon={BanknoteIcon}
            tone="revenue"
            isLoading={byInstructorLoading}
            value={
              summary.estimatedPayout > 0 ? (
                <CompactMoneyAmount amount={summary.estimatedPayout} />
              ) : (
                "—"
              )
            }
          />
        </ReportSummaryStatGrid>

        {barChartData.length > 0 && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>ຈຳນວນຄັ້ງ ຕາມຄູສອນ</CardTitle>
              <p className="text-muted-foreground text-sm">
                ສະແດງຈຳນວນຄັ້ງ ທີ່ສອນແຕ່ລະຄູ
              </p>
            </CardHeader>
            <CardContent>
              <BarChart
                data={barChartData}
                config={barChartConfig}
                xDataKey="instructor"
                valueDataKey="sessionsTaught"
                xAxisTickFormatter={(v) =>
                  typeof v === "string" && v.length > 12
                    ? `${v.slice(0, 10)}...`
                    : String(v)
                }
                className="mx-auto aspect-video max-h-[300px]"
              />
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader>
            <CardTitle>ຕາຕະລາງແຍກຕາມຄູສອນ</CardTitle>
            <p className="text-muted-foreground text-sm">
              ຈຳນວນຄັ້ງ ອັດຕາແລະປະເມີນຄ່າຈ້າງແຕ່ລະຄູ (ຄິດຕໍ່ຊົ່ວໂມງ)
            </p>
          </CardHeader>
          <CardContent>
            <InstructorSessionReportTable
              isLoading={byInstructorLoading}
              data={byInstructor}
            />
          </CardContent>
        </Card>
      </Main>
    </>
  );
}
